import React from 'react';
import { useSystemStatus } from '../api/hooks';
import './StatusPage.css';

const STATUS_ICONS = {
  ok: '\u2713',
  error: '\u2717',
  disabled: '\u2014',
  degraded: '!',
};

const STATUS_LABELS = {
  postgres: 'PostgreSQL',
  optic_sse: 'OpticOdds SSE',
  optic_api: 'OpticOdds API',
  bigquery: 'BigQuery',
  slack: 'Slack',
};

function formatUptime(seconds) {
  if (!seconds) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function CheckRow({ name, check }) {
  if (!check) return null;

  // BigQuery has nested checks
  if (check.checks) {
    return (
      <div className="check-group">
        <div className="check-row">
          <span className={`check-icon status-${check.riskhome_dataset?.status === 'ok' && check.sleeper_reporting?.status === 'ok' ? 'ok' : 'error'}`}>
            {check.riskhome_dataset?.status === 'ok' && check.sleeper_reporting?.status === 'ok' ? STATUS_ICONS.ok : STATUS_ICONS.error}
          </span>
          <span className="check-name">{STATUS_LABELS[name] || name}</span>
          {check.project && <span className="check-detail">Project: {check.project}</span>}
        </div>
        <div className="check-sub-items">
          {Object.entries(check.checks).map(([subName, subCheck]) => (
            <div key={subName} className="check-row check-sub">
              <span className={`check-icon status-${subCheck.status}`}>
                {STATUS_ICONS[subCheck.status] || '?'}
              </span>
              <span className="check-name">{subName}</span>
              <span className="check-detail">
                {subCheck.dataset || subCheck.message || ''}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="check-row">
      <span className={`check-icon status-${check.status}`}>
        {STATUS_ICONS[check.status] || '?'}
      </span>
      <span className="check-name">{STATUS_LABELS[name] || name}</span>
      <span className="check-detail">{check.message || ''}</span>
    </div>
  );
}

function StatusPage() {
  const { data, isLoading, error, dataUpdatedAt } = useSystemStatus({ refetchInterval: 10_000 });

  if (isLoading && !data) {
    return (
      <div className="status-page">
        <div className="loading">Loading status...</div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="status-page">
        <div className="error-message">
          Failed to fetch status: {error.message}
        </div>
      </div>
    );
  }

  const overallStatus = data?.status || 'unknown';
  const checks = data?.checks || {};

  return (
    <div className="status-page">
      <div className="page-header">
        <h1>Service Status</h1>
        {dataUpdatedAt && (
          <div className="last-update">
            Updated: {new Date(dataUpdatedAt).toLocaleTimeString()}
          </div>
        )}
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className={`stat-value stat-${overallStatus === 'ok' ? 'success' : overallStatus === 'degraded' ? 'warning' : 'error'}`}>
            {overallStatus.toUpperCase()}
          </div>
          <div className="stat-label">Overall</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{formatUptime(data?.uptime_s)}</div>
          <div className="stat-label">Uptime</div>
        </div>
        <div className="stat-card">
          <div className="stat-value stat-success">
            {Object.values(checks).filter(c => c.status === 'ok' || (c.checks && Object.values(c.checks).every(sc => sc.status === 'ok' || sc.status === 'disabled'))).length}
          </div>
          <div className="stat-label">Healthy</div>
        </div>
        <div className="stat-card">
          <div className="stat-value stat-error">
            {Object.values(checks).filter(c => c.status === 'error' || (c.checks && Object.values(c.checks).some(sc => sc.status === 'error'))).length}
          </div>
          <div className="stat-label">Issues</div>
        </div>
      </div>

      <div className="card">
        <h2 className="card-title">Dependency Checks</h2>
        <div className="checks-list">
          {Object.entries(checks).map(([name, check]) => (
            <CheckRow key={name} name={name} check={check} />
          ))}
        </div>
      </div>
    </div>
  );
}

export default StatusPage;
